import { getCatalogEditorModelPath } from "@/v2-backoffice-client/feature/configuration/feature.configuration.constants"
import type {
  TranslationRow,
  TypeValueAttributes,
} from "@/v2-backoffice-client/translation/translation.types"

/** An empty object, not an empty file: what an editor can parse and add a key to. */
export const EMPTY_ATTRIBUTES = "{}"

/** Shown when an operator has to be told what shape the field expects. */
const EXAMPLE_ATTRIBUTES = '{"Level": 2}'

/**
 * Monaco model for one type set's Attributes document, built from the shared
 * catalog-editor base so it cannot collide with a registered JSON schema.
 *
 * Deliberately schema-less, and per type set rather than one model for all of
 * them: the keys are the columns of whichever table the seed writes into, so
 * two sets have nothing in common to validate against — but keeping the model
 * distinct means Monaco's undo history and any future per-set schema follow the
 * set rather than leaking across them.
 */
export function getAttributesEditorModelPath(typeSet: string): string {
  return getCatalogEditorModelPath(`type-value-attributes/${typeSet}.json`)
}

/** How many distinct values per key the shape hint lists before eliding. */
const MAX_HINT_VALUES = 4

/** One attribute key as it is used across a type set. */
export type AttributeKeyUsage = {
  key: string
  /** How many values in the set carry the key. */
  count: number
  /** Distinct values seen, capped at MAX_HINT_VALUES and rendered as JSON. */
  examples: string[]
  /** True when more distinct values exist than `examples` lists. */
  hasMoreExamples: boolean
}

/**
 * How the values of one type set actually use their Attributes column, most
 * widely used key first.
 *
 * This is derived from the set's own rows rather than declared anywhere,
 * because nothing declares it: `common.TypeValues.Attributes` is free JSON and
 * the keys a type table needs are the columns of the table the seed writes
 * into. The rows that already seed correctly are therefore the only available
 * description of the shape — which is exactly what an operator adding the next
 * value needs to match.
 */
export function summarizeAttributeKeys(
  rows: TranslationRow[],
): AttributeKeyUsage[] {
  const counts = new Map<string, number>()
  const values = new Map<string, Set<string>>()

  for (const row of rows) {
    if (!row.attributes) continue
    for (const [key, value] of Object.entries(row.attributes)) {
      counts.set(key, (counts.get(key) ?? 0) + 1)
      const seen = values.get(key) ?? new Set<string>()
      seen.add(JSON.stringify(value))
      values.set(key, seen)
    }
  }

  return [...counts.entries()]
    .map(([key, count]) => {
      const distinct = [...(values.get(key) ?? [])]
      return {
        key,
        count,
        examples: distinct.slice(0, MAX_HINT_VALUES),
        hasMoreExamples: distinct.length > MAX_HINT_VALUES,
      }
    })
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key))
}

/**
 * A starter Attributes document for a new value of this type set: every key
 * its siblings use, with null values to fill in.
 *
 * Null rather than a borrowed sibling value on purpose. A pre-filled
 * `"CategoryCode": "ADM"` reads as answered and would ship whatever the
 * operator did not look at; null reads as a blank to complete, and the seed
 * rejects it if it is left that way. The values actually in use are offered
 * separately as a hint (see summarizeAttributeKeys).
 *
 * Returns null when no value in the set has attributes — there is no shape to
 * suggest, and an empty `{}` says that more honestly than a guess.
 */
export function buildAttributesTemplate(
  rows: TranslationRow[],
): TypeValueAttributes | null {
  const keys = summarizeAttributeKeys(rows)
  if (keys.length === 0) return null
  return Object.fromEntries(keys.map((usage) => [usage.key, null]))
}

/** Pretty-prints attributes for the editor, or `{}` when there are none. */
export function toAttributesDocument(
  attributes: TypeValueAttributes | null,
): string {
  if (!attributes) return EMPTY_ATTRIBUTES
  return JSON.stringify(attributes, null, 2)
}

export type ParsedAttributes =
  | { ok: true; attributes: TypeValueAttributes }
  | { ok: false; error: string }

/**
 * Reads a hand-typed Attributes document, rejecting what the column cannot
 * usefully hold.
 *
 * A JSON array or bare scalar parses fine and would store fine — the column
 * only checks ISJSON — but the seed reads attributes by key, so anything that
 * is not an object is data no type table can consume. Refusing it at the field
 * is the only place an operator finds out before the next publish fails.
 *
 * An empty document is `{}`, never null: null is the wire value that means
 * "leave the stored attributes alone" (api.Upsert_Common_TypeValue's ISNULL
 * contract), so an operator who empties the field and saves would silently get
 * the old document back instead of the clear they asked for.
 */
export function parseAttributes(document: string): ParsedAttributes {
  const trimmed = document.trim()
  if (trimmed === "") return { ok: true, attributes: {} }

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Invalid JSON.",
    }
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return {
      ok: false,
      error: `Attributes must be a JSON object of column names, e.g. ${EXAMPLE_ATTRIBUTES}.`,
    }
  }

  return { ok: true, attributes: parsed as TypeValueAttributes }
}
